import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, ClipboardCheck, Search, MessageSquare as MessageSquareCheck } from 'lucide-react';

export function AbusePage() {
  const { t } = useTranslation();

  return (
    <div className="pt-16">
      <div className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-6">{t('abuse.title')}</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            {t('abuse.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">{t('abuse.howWeHandle')}</h2>
          <p className="text-xl text-gray-600">
            {t('abuse.sendReport')} <a href="mailto:abuse@synthohosting.net" className="text-blue-600 hover:text-blue-700 font-semibold">abuse@synthohosting.net</a>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {t('abuse.legalNotice')} <a href="mailto:multiservice.23@pec.it" className="text-blue-600 hover:text-blue-700">multiservice.23@pec.it</a>
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-8 relative">
          {/* Connecting line */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-blue-200 hidden md:block" />
          
          {/* Step 1 */}
          <div className="relative bg-white rounded-xl shadow-lg p-6 transform transition-all duration-300 hover:-translate-y-2">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-600 rounded-full p-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">{t('abuse.steps.reception.title')}</h3>
              <p className="text-gray-600">
                {t('abuse.steps.reception.description')}
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative bg-white rounded-xl shadow-lg p-6 transform transition-all duration-300 hover:-translate-y-2">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-600 rounded-full p-4">
              <ClipboardCheck className="w-8 h-8 text-white" />
            </div>
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">{t('abuse.steps.review.title')}</h3>
              <p className="text-gray-600">
                {t('abuse.steps.review.description')}
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="relative bg-white rounded-xl shadow-lg p-6 transform transition-all duration-300 hover:-translate-y-2">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-600 rounded-full p-4">
              <Search className="w-8 h-8 text-white" />
            </div>
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">{t('abuse.steps.investigation.title')}</h3>
              <p className="text-gray-600">
                {t('abuse.steps.investigation.description')}
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="relative bg-white rounded-xl shadow-lg p-6 transform transition-all duration-300 hover:-translate-y-2">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-600 rounded-full p-4">
              <MessageSquareCheck className="w-8 h-8 text-white" />
            </div>
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">{t('abuse.steps.resolution.title')}</h3>
              <p className="text-gray-600">
                {t('abuse.steps.resolution.description')}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-20 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-2xl font-bold mb-6">{t('abuse.requiredInfo.title')}</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('abuse.requiredInfo.subtitle')}</h4>
              <ul className="space-y-3">
                {t('abuse.requiredInfo.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index} className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('abuse.commitment.title')}</h4>
              <ul className="space-y-3">
                {t('abuse.commitment.items', { returnObjects: true }).map((item: string, index: number) => (
                  <li key={index} className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}